import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Button,
  IconButton,
  Typography,
  Box,
  Checkbox,
  CircularProgress,
  Tooltip,
  Grid,
} from "@mui/material";
import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { UserContext } from "../../../stores/User/User.provider";
import useStore from "../../../helpers/useStore";
import { GiftCertificateDto } from "../../../api-client";
import { SalesStoreContext } from "../../../stores/Sales/sales.provider";
import FlatCard from "../../../atoms/FlatCard";
import GenericTable from "../SelectableTable";
import { MobiliAvatar } from "../../../atoms/MobiliAvatar";
import { CheckCircle, Delete, Warning } from "@mui/icons-material";
import CheckOutDialog from "../CheckOutDialog";
import _ from "lodash";
import { TherapyChip } from "../../../atoms/Chip";
import WarningDialog from "../../../atoms/WarningDialog";
import { methodColorKey } from "../../../helpers/slotTherapy";

const GCTable = observer(
  ({
    dataRefreshFlag,
    setDataRefreshFlag,
  }: {
    dataRefreshFlag: boolean;
    setDataRefreshFlag: any;
  }) => {
    const [giftCertificates, setGiftCertificates] = useState<GiftCertificateDto[]>([]);
    const [selectedGiftCertificates, setSelectedGiftCertificates] = useState<GiftCertificateDto[]>(
      []
    );
    const [openDialog, setOpenDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [checkoutSummary, setCheckoutSummary] = useState<any[]>([]);
    const [gcToDelete, setGcToDelete] = useState<GiftCertificateDto | null>(null);
    const [deleting, setDeleting] = useState(false);

    const SalesStore = useStore(SalesStoreContext);
    const UserStore = useStore(UserContext);

    const columns = [
      {
        label: "Code",
        render: (item: any) => item.codeLowSecurity,
      },
      {
        label: "Description",
        render: (item: any) => (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {item.items?.map((it: any, index: number) => (
              <TherapyChip
                key={index}
                therapyName={it.therapy?.abbreviation || ""}
                colorKey={
                  it.therapy?.therapyMethodId
                    ? methodColorKey(it.therapy.therapyMethodId)
                    : undefined
                }
              />
            ))}
          </Box>
        ),
      },

      {
        label: "Preis (€)",
        render: (item: any) => item.value ?? "-",
      },
      {
        label: "Mitarbeiter",
        render: (item: any) =>
          item.createdByUser ? (
            <Tooltip title={`${item.createdByUser.firstName} ${item.createdByUser.lastName}`}>
              <MobiliAvatar
                user={item?.createdByUser}
                sx={{ width: 25, height: 25, fontSize: "0.8em" }}
              />
            </Tooltip>
          ) : (
            "-"
          ),
      },
      {
        label: "Status",
        render: (item: any) =>
          item.transaction ? (
            <Tooltip title={item.transaction.id}>
              <CheckCircle color="success" />
            </Tooltip>
          ) : (
            <Tooltip title="Unbezahlt">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="body2">Unbezahlt</Typography>
                <Warning color="warning" />
              </Box>
            </Tooltip>
          ),
      },
      {
        label: "",
        render: (item: any) =>
          !item.transaction && (
            <Tooltip title="Unbezahlten Gutschein löschen">
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  setGcToDelete(item);
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          ),
      },
    ];

    useEffect(() => {
      const fetchData = async () => {
        try {
          setLoading(true);
          const data = await SalesStore.getUnpaidGiftCertificates();
          setGiftCertificates(data);

        // Auto-select the most recent by createdAt
      if (data && data.length > 0) {
        const mostRecent = data.reduce((latest:any, item:any) =>
          new Date(item.createdAt) > new Date(latest.createdAt) ? item : latest
        );
        setSelectedGiftCertificates([mostRecent]);
      }
        } catch (error) {
          // console.log("Error fetching certificates");
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }, [dataRefreshFlag]);

    const toggleSelect = (gc: GiftCertificateDto) => {
      setSelectedGiftCertificates((prev) =>
        prev.find((item) => item.id === gc.id)
          ? prev.filter((item) => item.id !== gc.id)
          : [...prev, gc]
      );
    };

    //  const handleCheckout = () => {
    //   const groupedByCode = _.groupBy(selectedGiftCertificates, (gc: any) => gc.codeLowSecurity);

    //   const summary = Object.entries(groupedByCode).map(([code, certificates]) => {
    //     const unitPrice = Number(certificates[0]?.value || 0);
    //     const quantity = certificates.length;

    //     return {
    //       giftCardCode: code,
    //       unitPrice,
    //       quantity,
    //       total: unitPrice * quantity,
    //     };
    //   });

    //   setCheckoutSummary(summary);
    //   setOpenDialog(true);
    // };
    const handleCheckout = () => {
      // Step 1: Flatten all therapies from selected gift certificates
      const allTherapyItems = selectedGiftCertificates.flatMap((gc) => {
        const unitPricePerItem = gc.value
          ? Number(gc.value) / (gc.items?.reduce((sum, i: any) => sum + i.amount, 0) || 1)
          : 0;

        return (gc.items || []).map((item: any) => ({
          therapyId: item.therapy?.id,
          abbreviation: item.therapy?.abbreviation || "-",
          amount: item.amount || 0,
          unitPrice: unitPricePerItem,
        }));
      });

      // Step 2: Group by therapyId
      const grouped = _.groupBy(allTherapyItems, (item) => item.therapyId);

      // Step 3: Generate summary
      const summary = Object.entries(grouped).map(([therapyId, items]) => {
        const abbreviation = items[0]?.abbreviation || "-";
        const unitPrice = items[0]?.unitPrice || 0;
        const quantity = items.reduce((sum, item) => sum + item.amount, 0);

        return {
          therapyId,
          abbreviation,
          unitPrice,
          quantity,
          total: unitPrice * quantity,
        };
      });

      setCheckoutSummary(summary);
      setOpenDialog(true);
    };

    const handleDialogClose = () => {
      setOpenDialog(false);
      setSelectedGiftCertificates([]);
      setCheckoutSummary([]);
    };

    const handleConfirmDelete = async () => {
      if (!gcToDelete) return;
      setDeleting(true);
      const success = await SalesStore.deleteGiftCertificate(gcToDelete.id!);
      setDeleting(false);
      if (success) {
        setSelectedGiftCertificates((prev) => prev.filter((gc) => gc.id !== gcToDelete.id));
        setDataRefreshFlag(!dataRefreshFlag);
      }
      setGcToDelete(null);
    };

    const totalAmount = selectedGiftCertificates.reduce(
      (sum, gc) => sum + Number(gc.value || 0),
      0
    );

    const handleRowSelection = (item: any) => {
      setSelectedGiftCertificates((prevSelectedRows) => {
        const isSelected = prevSelectedRows.includes(item);
        return isSelected
          ? prevSelectedRows.filter((row) => row !== item)
          : [...prevSelectedRows, item];
      });
    };

    const handleSelectAll = () => {
      if (selectedGiftCertificates.length === giftCertificates.length) {
        setSelectedGiftCertificates([]);
      } else {
        setSelectedGiftCertificates(giftCertificates);
      }
    };

    return (
      <>
        {loading ? (
          <Typography sx={{ textAlign: "center", mt: 4 }}>
            <CircularProgress />
          </Typography>
        ) : giftCertificates.length === 0 ? (
          <Typography variant="body1">Keine unbezahlten Gutscheine gefunden.</Typography>
        ) : (
          <FlatCard title={undefined}>
            <GenericTable
              columns={columns}
              data={giftCertificates}
              isLoading={loading}
              emptyMessage="Keine Dienste gefunden."
              onRowClick={handleRowSelection}
              isSelected={(item) => selectedGiftCertificates.includes(item)}
            />
            <Grid container sx={{ mt: 1, px: 2 }} spacing={2}>
              <Grid item xs={12} />
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Ausgewählt:</strong> {selectedGiftCertificates.length}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Gesamtbetrag:</strong> €{totalAmount.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Button variant="contained" onClick={handleSelectAll} sx={{ whiteSpace: "nowrap" }}>
                  {selectedGiftCertificates.length === giftCertificates.length
                    ? "Abwählen"
                    : "Alle auswählen"}
                </Button>
              </Grid>
              <Grid item xs={3}>
                {selectedGiftCertificates.length === 0 && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: "block", mt: 1, fontSize: "12px" }}
                  >
                    Bitte wählen Sie die Gutscheine aus, die bezahlt werden sollen.
                  </Typography>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCheckout}
                  sx={{ mt: { xs: 2, sm: 0 } }}
                  disabled={selectedGiftCertificates.length === 0}
                >
                  In den Warenkorb legen
                </Button>
              </Grid>
            </Grid>
          </FlatCard>
        )}

        <WarningDialog
          open={!!gcToDelete}
          onClose={() => setGcToDelete(null)}
          onContinue={handleConfirmDelete}
          title="Gutschein löschen"
          content={`Möchten Sie den unbezahlten Gutschein "${gcToDelete?.codeLowSecurity}" (€${gcToDelete?.value}) wirklich löschen?`}
          loading={deleting}
        />

        <CheckOutDialog
          open={openDialog}
          onClose={handleDialogClose}
          isGiftCardCheckOut={true}
          checkoutSummary={checkoutSummary}
          selectedServices={selectedGiftCertificates}
          dataRefreshFlag={dataRefreshFlag}
          setDataRefreshFlag={setDataRefreshFlag}
        />
      </>
    );
  }
);

export default GCTable;
